import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import {
  AfterVisitSummaryCollapseConnected,
  usePatientReflectionOverviewConnected,
} from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { appointmentInfoAtom } from '../../state';
import { roomAtom } from '../../video-sdk';
import { ReflectionTopicsDrawer } from './ReflectionTopicsDrawer';
import { useHandleReflectionTopicCompletionCallback } from './useHandleReflectionTopicCompletionCallback';
import { useReflectionDrawerVisibility } from './useReflectionDrawerVisibility';
import { useTrackSessionTopicsViewed } from './useTrackSessionTopicsViewed';

export function ReflectionTopicsDrawerWrapper() {
  const t = useTranslate();
  const { patientShortId } = useParams();
  const { showAVS } = useReflectionDrawerVisibility();
  const isInSession = !!useAtomValue(roomAtom);
  const appointmentShortId = useAtomValue(appointmentInfoAtom)?.shortId ?? '';
  const { userViewed } = useTrackEvent();
  const handleReflectionTopicCompletion = useHandleReflectionTopicCompletionCallback();

  const { title, content, goToPreviousPatientReflectionView, hasPreviousPatientReflectionView } =
    usePatientReflectionOverviewConnected({
      patientShortId: patientShortId ?? '',
      onUpdateTopicCompletionStatusSuccess: handleReflectionTopicCompletion,
      isInverse: true,
    });
  useTrackSessionTopicsViewed(!!content);

  const onClickBackButton = hasPreviousPatientReflectionView ? goToPreviousPatientReflectionView : undefined;

  useEffect(
    function trackReflectionTopicsViewed() {
      userViewed(EventTypeEnum.REFLECTION_TOPICS_VIEWED, {
        patientShortId: patientShortId ?? '',
        inSession: isInSession,
      });
    },
    [patientShortId, userViewed, isInSession],
  );

  const drawerContent = (
    <>
      {showAVS && patientShortId && (
        <AfterVisitSummaryCollapseConnected
          patientShortId={patientShortId}
          appointmentShortId={appointmentShortId}
          inSession={isInSession}
          isInverse
        />
      )}
      {content}
    </>
  );

  return (
    <ReflectionTopicsDrawer
      title={title ?? t('telehealth-core:controls.sessionPrep')}
      content={drawerContent}
      onClickBackButton={onClickBackButton}
    />
  );
}
